import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";

import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AlertsContext from "common/providers/alerts";
import { Course } from "types/Course";
import useOrganization from "common/hooks/useOrganization";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import TabBlockItem from "common/components/TabBlockItem/TabBlockItem";
import {
  ClipboardDocumentCheckIcon,
  ExclamationCircleIcon,
  ListBulletIcon,
  SparklesIcon,
} from "@heroicons/react/20/solid";
import useCourse from "common/hooks/useCourse";
import { GiPathDistance } from "react-icons/gi";
import CourseItem from "./CourseItem";

function Courses() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);
  const { organization } = useOrganization();

  const { coursesNumber, refreshCoursesNumber } = useCourse();

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<Course>("courses/private/data", {
    defaultFilters: {
      organization: organization?.id,
    },
    defaultSort: "name:ASC",
    cachePrefix: "Courses",
  });
  const { removeItem } = useItem<Course>("courses/private/data", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/courses/edit/${id}`);
  };

  const handleDuplicate = (id: any) => {
    navigate(`/courses/duplicate/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$contains",
        filterLabel: t("filters.search"),
        columnClasses: "w-1/2",
      },
    ],
    [t]
  );

  const statusList = useMemo(() => {
    return [
      {
        id: "",
        name: t("status.all"),
        count: coursesNumber?.all || 0,
        icon: ListBulletIcon,
        color: "yellow",
      },
      {
        id: "draft",
        name: t("status.draft"),
        count: coursesNumber?.draft || 0,
        icon: ClipboardDocumentCheckIcon,
        color: "blue",
      },
      {
        id: "published",
        name: t("status.online"),
        count: coursesNumber?.published || 0,
        icon: SparklesIcon,
        color: "green",
      },
      {
        id: "disabled",
        name: t("status.closed"),
        count: coursesNumber?.disabled || 0,
        icon: ExclamationCircleIcon,
        color: "red",
      },
    ];
  }, [t, coursesNumber]);

  useEffect(() => {
    if (organization?.id) {
      refreshCoursesNumber(organization?.id);
    }
  }, [organization?.id, refreshCoursesNumber]);

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.courses")}
        subTitle={t("titleLegends.courses")}
        btnTitle={t("newItem.course")}
        btnSubtitle={t("newItem.courseLegend")}
        Icon={GiPathDistance}
        addLink="/courses/add"
        isBeforeFilters
        filters={filters}
        setFilter={setFilter}
        filterName="status$contains"
        FilterComponent={(props: any) => (
          <RadioBlock
            Component={TabBlockItem}
            items={statusList}
            horizontal
            noGap
            {...props}
          />
        )}
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={CourseItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        onDuplicate={handleDuplicate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Courses;
